/**
 * Kotak "Tugas saya hari ini" — T100 (Titipan auditor 1 Nov 2026).
 *
 * SATU daftar berisi setiap pekerjaan yang menunggu tangan pemakai (bukti transfer menunggu konfirmasi, serah
 * terima tertunda, tarif upah belum lengkap, barang jadi menunggu diserahkan, kabar WA belum sampai), paling lama
 * menunggu di atas, tiap baris punya tombol langsung ke layarnya. Sumber & cakupan per jabatan di backend
 * `services/tugas_hari_ini.py`. Jam memakai zona cabang pesanan + label WIB/WITA (T93).
 *
 * Seperti butir dasbor (K137): tidak ada tombol tutup — baris hilang hanya saat pekerjaannya selesai. Sumber yang
 * gagal dibaca DISEBUT, bukan dianggap kosong ("tidak ada tugas" palsu menenangkan orang yang salah).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { Card } from "../ui/card";
import { api } from "../../lib/api";
import { jamPesanan, useStoreTimezones } from "../../lib/timezone";

const LABEL_SUMBER = {
  BUKTI: "Bukti transfer", SERAH: "Serah terima", TARIF: "Tarif upah", AMBIL: "Barang jadi", WA: "Kabar WA",
};

/** MURNI — "3 hari" / "5 jam" / "baru saja" sejak waktu ISO. */
export function lamaMenunggu(iso, kini = Date.now()) {
  if (!iso) return null;
  const ms = kini - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const jam = Math.floor(ms / 3600000);
  if (jam >= 24) return `${Math.floor(jam / 24)} hari`;
  if (jam >= 1) return `${jam} jam`;
  return "baru saja";
}

export default function KotakTugasSaya() {
  useStoreTimezones();
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState(false);

  useEffect(() => {
    api.get("/dashboard/tugas-saya")
      .then(({ data: d }) => setData(d))
      .catch(() => setGalat(true));
  }, []);

  if (!data && !galat) return null;
  const items = data?.items || [];
  if (data?.sebab_kosong && !items.length && !(data?.gagal || []).length) return null;

  return (
    <Card className="p-5" data-testid="kotak-tugas-saya">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-5 w-5" />
        <h2 className="text-lg font-semibold">Tugas saya hari ini</h2>
        {items.length > 0 && <span className="text-sm text-muted-foreground">· {items.length}</span>}
      </div>
      {galat && (
        <p className="text-sm text-warning-soft-foreground" data-testid="tugas-saya-galat">
          Daftar tugas belum bisa dimuat. Muat ulang halaman sebentar lagi.
        </p>
      )}
      {(data?.gagal || []).length > 0 && (
        <p className="text-xs text-warning-soft-foreground mb-2" data-testid="tugas-saya-sumber-gagal">
          Sebagian belum terbaca: {data.gagal.map((s) => LABEL_SUMBER[s] || s).join(", ")}. Periksa layarnya langsung.
        </p>
      )}
      {data && !items.length && !(data.gagal || []).length && (
        <p className="text-sm text-muted-foreground" data-testid="tugas-saya-kosong">
          Tidak ada pekerjaan yang menunggu Anda saat ini.
        </p>
      )}
      <ul className="divide-y divide-border">
        {items.map((t, i) => (
          <li key={`${t.sumber}-${i}`} className="py-2 flex flex-wrap items-center justify-between gap-2"
              data-testid={`tugas-saya-${t.sumber}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{t.judul}</div>
              <div className="text-xs text-muted-foreground">
                {LABEL_SUMBER[t.sumber] || t.sumber}
                {t.rinci ? ` · ${t.rinci}` : ""}
                {t.menunggu_sejak ? ` · menunggu sejak ${jamPesanan(t.menunggu_sejak, t.store_id, t.region_id)}` : ""}
                {lamaMenunggu(t.menunggu_sejak) ? ` (${lamaMenunggu(t.menunggu_sejak)})` : ""}
              </div>
            </div>
            <Link to={t.tautan}
                  className="text-xs font-medium rounded-md border border-input px-3 py-1.5 hover:bg-accent"
                  data-testid={`tugas-saya-buka-${i}`}>
              {t.tombol || "Buka"}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
